import React, { useState, useEffect } from "react";

import Header from "./components/header/header.jsx";
import Sidebar from "./components/sidebar/sidebar.jsx";
import Content from "./components/content/content.jsx";

import img from "./assets/default.jpg";

import { AppContext } from "./context/AppContext.jsx";
import { slideToggle } from "./composables/slideToggle.js";

import { toast, ToastContainer } from "react-toastify";
import { addLocale } from "primereact/api";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useNavigate } from "react-router-dom";
import { getIsTrial, getProfilePhoto } from "./utils/auth.js";

const App = () => {
  const navigate = useNavigate();
  const [appTheme] = useState("");
  const [appDarkMode, setAppDarkMode] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(img);
  const [appHeaderNone, setAppHeaderNone] = useState(false);
  const [appHeaderFixed, setAppHeaderFixed] = useState(true);
  const [appHeaderInverse, setAppHeaderInverse] = useState(false);
  const [hasScroll, setHasScroll] = useState(false);
  const [appSidebarNone, setAppSidebarNone] = useState(false);
  const [appSidebarWide] = useState(false);
  const [appSidebarLight] = useState(false);
  const [appSidebarMinify, setAppSidebarMinify] = useState(false);
  const [appSidebarMobileToggled, setAppSidebarMobileToggled] = useState(false);
  const [appSidebarTransparent] = useState(false);
  const [appSidebarSearch] = useState(false);
  const [appSidebarFixed, setAppSidebarFixed] = useState(true);
  const [appContentNone, setAppContentNone] = useState(false);
  const [appContentClass, setAppContentClass] = useState("");
  const [appTopMenu] = useState(false);
  const [appTopMenuMobileToggled] = useState(false);
  const [appSidebarEndToggled] = useState(false);
  const [appSidebarEndMobileToggled, setAppSidebarEndMobileToggled] =
    useState(false);

  const [menu, setMenu] = useState(() => localStorage.getItem("menu"));
  const [permissions, setPermissions] = useState(() =>
    localStorage.getItem("permissions")
  );
  const fixedNomenclatures = [
    {
      value: "Quadra",
      label: "Quadra",
    },
    {
      value: "Lote",
      label: "Lote",
    },
  ];

  const [nomenclatures, setNomenclatures] = useState(
    () => localStorage.getItem("nomenclatures") || JSON.stringify(fixedNomenclatures)
  );

  const [lastNomenclatures, setLastNomenclatures] = useState(
    () => localStorage.getItem("lastNomenclatures") || JSON.stringify(fixedNomenclatures)
  );

  useEffect(() => {
    const photo = getProfilePhoto();
    if (profilePhoto !== photo) {
      setProfilePhoto(photo);
    }
  }, [profilePhoto]);

  useEffect(() => {
    localStorage.setItem("menu", menu);
  }, [menu]);

  useEffect(() => {
    localStorage.setItem("permissions", permissions);
  }, [permissions]);

  useEffect(() => {
    handleEditNomenclatures();
    localStorage.setItem("nomenclatures", nomenclatures);
    localStorage.setItem("lastNomenclatures", nomenclatures);
    setLastNomenclatures(JSON.parse(nomenclatures));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nomenclatures]);

  useEffect(() => {
    if (appDarkMode) {
      handleSetAppDarkMode(true);
    }
    window.addEventListener("scroll", handleScroll);

    if (localStorage) {
      if (localStorage.appTheme !== undefined) {
        document.body.classList.add("theme-" + localStorage.appTheme);
      }
      if (localStorage.appDarkMode !== undefined) {
        handleSetAppDarkMode(
          localStorage.appDarkMode === "true" ? true : false
        );
      }
      if (localStorage.appSidebarMinify !== undefined) {
        handleSetAppSidebarMinified(
          localStorage.appSidebarMinify === "true" ? true : false
        );
      }
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [appDarkMode]);

  useEffect(() => {
    let toastId;
    let lastPathname = localStorage.getItem("lastPathname");

    const handleConnectionChange = () => {
      // console.log("Navigator: ", navigator);
      if (navigator.onLine) {
        // Se estiver online
        if (toastId) {
          toast.update(toastId, {
            render: "Conexão restabelecida!",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 2000,
          });
          toastId = undefined;
          if (lastPathname && window.location.pathname !== lastPathname) {
            navigate(lastPathname);
          }
          localStorage.removeItem("lastPathname");
        }
      } else {
        // Se estiver offline
        if (!toastId) {
          toastId = toast.loading(
            "Sua conexão caiu! Estamos buscando reconectar..."
          );
          localStorage.setItem("lastPathname", window.location.pathname);
          lastPathname = window.location.pathname;
        }
      }
    };

    window.addEventListener("online", handleConnectionChange);
    window.addEventListener("offline", handleConnectionChange);
    handleConnectionChange();

    return () => {
      window.removeEventListener("online", handleConnectionChange);
      window.removeEventListener("offline", handleConnectionChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = () => {
    if (window.scrollY > 0) {
      setHasScroll(true);
    } else {
      setHasScroll(false);
    }
    var elm = document.getElementsByClassName("nvtooltip");
    for (var i = 0; i < elm.length; i++) {
      elm[i].classList.add("d-none");
    }
  };

  const toggleAppSidebarMinify = (e) => {
    e.preventDefault();
    setAppSidebarMinify(!appSidebarMinify);
    if (localStorage) {
      localStorage.appSidebarMinify = !appSidebarMinify;
    }
  };

  const toggleAppSidebarMobile = (e) => {
    e.preventDefault();
    setAppSidebarMobileToggled(!appSidebarMobileToggled);
  };

  const handleSetAppSidebarNone = (value) => {
    setAppSidebarNone(value);
  };

  const handleSetAppSidebarMinified = (value) => {
    setAppSidebarMinify(value);
  };

  const toggleAppSidebarEndMobile = (e) => {
    e.preventDefault();
    setAppSidebarEndMobileToggled(!appSidebarEndMobileToggled);
  };

  const handleSetAppContentNone = (value) => {
    setAppContentNone(value);
  };

  const handleSetAppContentClass = (value) => {
    setAppContentClass(value);
  };

  const handleSetAppHeaderNone = (value) => {
    setAppHeaderNone(value);
  };

  const handleSetAppHeaderFixed = (value) => {
    if (value === false && appSidebarFixed) {
      setAppSidebarFixed(false);
      if (localStorage) {
        localStorage.appSidebarFixed = false;
      }
    }
    setAppHeaderFixed(value);
    if (localStorage) {
      localStorage.appHeaderFixed = value;
    }
  };

  const handleSetAppHeaderInverse = (value) => {
    setAppHeaderInverse(value);
    if (localStorage) {
      localStorage.appHeaderInverse = value;
    }
  };

  const toggleAppTopMenuMobile = (e) => {
    e.preventDefault();
    slideToggle(document.querySelector(".app-top-menu"));
  };

  const handleSetAppBoxedLayout = (value) => {
    if (value === true) {
      document.body.classList.add("boxed-layout");
    } else {
      document.body.classList.remove("boxed-layout");
    }
  };

  const handleSetAppDarkMode = (value) => {
    if (value === true) {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }
    setAppDarkMode(value);
    if (localStorage) {
      localStorage.appDarkMode = value;
    }
  };

  const handleEditNomenclatures = () => {
    // console.log(menu)
    const newMenu = JSON.parse(menu);
    const nomenclature = JSON.parse(nomenclatures);
    // console.log(nomenclature)
    // console.log(newMenu)
    // console.log(lastNomenclatures)
    if (nomenclature && lastNomenclatures && newMenu) {
      Object.keys(newMenu).forEach((key) => {
        if (newMenu[key].name === "Relatórios") {
          Object.keys(newMenu[key].subMenu).forEach((subKey) => {
            //            // console.log(newMenu[key].subMenu[subKey])
            if (
              newMenu[key].subMenu[subKey].name ===
              `${lastNomenclatures[1].value}(s) Disponíveis`
            ) {
              //              // console.log(nomenclature[1].value);
              newMenu[key].subMenu[
                subKey
              ].name = `${nomenclature[1].value}(s) Disponíveis`;
            }
            if (
              newMenu[key].subMenu[subKey].name ===
              `${lastNomenclatures[1].value}(s)`
            ) {
              //              // console.log(nomenclature[1].value);
              newMenu[key].subMenu[subKey].name = `${nomenclature[1].value}(s)`;
            }
          });
        }
        if (newMenu[key].name === "Configurações") {
          Object.keys(newMenu[key].subMenu).forEach((subKey) => {
            if (
              newMenu[key].subMenu[subKey].name ===
              `Ordenar ${lastNomenclatures[0].value} e ${lastNomenclatures[1].value}`
            ) {
              //              // console.log(nomenclature);
              newMenu[key].subMenu[
                subKey
              ].name = `Ordenar ${nomenclature[0].value} e ${nomenclature[1].value}`;
            }
            if (
              newMenu[key].subMenu[subKey].name ===
              `Editar Nomenclatura ${lastNomenclatures[0].value}/${lastNomenclatures[1].value}`
            ) {
              //              // console.log(nomenclature);
              newMenu[key].subMenu[
                subKey
              ].name = `Editar Nomenclatura ${nomenclature[0].value}/${nomenclature[1].value}`;
            }
          });
        }
      });
    }
    // console.log("Menu: ", newMenu);
    setMenu(JSON.stringify(newMenu));
    localStorage.setItem("menu", JSON.stringify(newMenu));
  };

  //traduzindo componentes do prime react para Portuguese
  addLocale("pt", {
    firstDayOfWeek: 1,
    dateFormat: "dd/mm/yy",
    dayNames: [
      "Domingo",
      "Segunda-feira",
      "Terça-feira",
      "Quarta-feira",
      "Quinta-feira",
      "Sexta-feira",
      "Sábado",
    ],
    dayNamesShort: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
    dayNamesMin: ["D", "S", "T", "Q", "Q", "S", "S"],
    monthNames: [
      "Janeiro",
      "Fevereiro",
      "Março",
      "Abril",
      "Maio",
      "Junho",
      "Julho",
      "Agosto",
      "Setembro",
      "Outubro",
      "Novembro",
      "Dezembro",
    ],
    monthNamesShort: [
      "Jan",
      "Fev",
      "Mar",
      "Abr",
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
    ],
    today: "Hoje",
    clear: "Limpar",
    //...
  });
  const isTrial = parseInt(getIsTrial());

  return (
    <AppContext.Provider
      value={{
        appTheme,
        appDarkMode,
        appHeaderNone,
        appHeaderFixed,
        appHeaderInverse,
        hasScroll,
        handleSetAppHeaderNone,
        handleSetAppHeaderInverse,
        handleSetAppHeaderFixed,
        appSidebarNone,
        appSidebarWide,
        appSidebarLight,
        appSidebarMinify,
        appSidebarMobileToggled,
        appSidebarTransparent,
        appSidebarSearch,
        appSidebarFixed,
        handleSetAppSidebarNone,
        handleSetAppSidebarMinified,
        toggleAppSidebarMinify,
        toggleAppSidebarMobile,
        appContentNone,
        appContentClass,
        handleSetAppContentNone,
        handleSetAppContentClass,
        appTopMenu,
        appTopMenuMobileToggled,
        toggleAppTopMenuMobile,
        appSidebarEndToggled,
        appSidebarEndMobileToggled,
        toggleAppSidebarEndMobile,
        handleSetAppBoxedLayout,
        handleSetAppDarkMode,
        menu,
        setMenu,
        nomenclatures,
        setNomenclatures,
        permissions,
        setPermissions,
        profilePhoto,
        setProfilePhoto,
      }}
    >
      <div
        className={`app ${appHeaderNone ? "app-without-header" : ""} ${appHeaderFixed && !appHeaderNone ? "app-header-fixed" : ""
          } ${appSidebarFixed ? "app-sidebar-fixed" : ""} ${appSidebarNone ? "app-without-sidebar" : ""
          } ${appSidebarMinify ? "app-sidebar-minified" : ""} ${appSidebarMobileToggled ? "app-sidebar-mobile-toggled" : ""
          } ${appSidebarEndMobileToggled ? "app-sidebar-end-mobile-toggled" : ""
          } ${hasScroll ? "has-scroll" : ""}`}
        style={{ paddingTop: !!isTrial && isTrial > 0 && "74px" }}
      >
        {!appHeaderNone && <Header />}
        {!appSidebarNone && <Sidebar />}
        {!appContentNone && <Content />}
        <ConfirmDialog />
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </div>
    </AppContext.Provider>
  );
};

export default App;
